import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, Package, Plus, Zap } from 'lucide-react';
import type { RecordDeletionGateway, TenantSummary } from '@forq/contracts';
import type { CustomersGateway } from '@forq/customers-data';
import { SalesPage } from '@forq/sales';
import type { SalesGateway } from '@forq/sales-data';
import type { FinanceGateway } from '@forq/finance-data';
import type { CompanyProfileGateway } from '@forq/platform-data';
import type { MachinesGateway } from '@forq/machines-data';
import type { MasterdataGateway } from '@forq/masterdata-data';
import type { MastersGateway } from '@forq/masters-data';

interface SalesCenterWorkspaceProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly enabledFeatureIds: readonly string[];
  readonly gateway: SalesGateway;
  readonly deletionGateway: RecordDeletionGateway;
  readonly customersGateway: CustomersGateway;
  readonly financeGateway: FinanceGateway;
  readonly companyGateway: CompanyProfileGateway;
  readonly machinesGateway: MachinesGateway;
  readonly masterdataGateway: MasterdataGateway;
  readonly mastersGateway: MastersGateway;
}

/**
 * Verkauf-Center = Kopf mit den echten Aktionen (Lead / Direktverkauf / Angebot) + die echte SalesPage.
 * Die frühere 9-Reiter-Attrappe (erfundene Zeilen, hartcodierte Cockpit-/Controlling-Zahlen, tote
 * Filter/Spalten-Buttons) ist entfernt -- SalesPage bringt ihre eigene funktionierende Bereichs-
 * navigation (Übersicht/Pipeline/Angebote/Aufträge/Übergaben) samt echter Filter mit.
 */
export function SalesCenterWorkspace(props: SalesCenterWorkspaceProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  return (
    <div className="sales-center">
      <header className="sales-center__header">
        <div>
          <span>FORQ Verkauf</span>
          <h1>Verkauf</h1>
          <p>Anfragen, Angebote, Maschinenverkauf und Auslieferungen in einem durchgängigen Vertriebsprozess.</p>
        </div>
        <div>
          <small>{props.tenant.shortName} · {props.mode === 'demo' ? 'Vorschau' : 'Supabase'}</small>
          <button type="button" onClick={() => navigate('/verkauf?aktion=lead')}><Plus size={14} /> Lead</button>
          <button type="button" onClick={() => navigate('/verkauf?aktion=direktverkauf')}><Zap size={14} /> Direktverkauf</button>
          <button type="button" onClick={() => navigate('/verkauf?aktion=paket')}><Package size={14} /> Paketangebot</button>
          <button type="button" className="is-primary" onClick={() => navigate('/verkauf?aktion=angebot')}><FileText size={14} /> Angebot</button>
        </div>
      </header>

      <SalesPage
        {...props}
        pendingAction={searchParams.get('aktion')}
        onActionConsumed={() => setSearchParams({}, { replace: true })}
      />
    </div>
  );
}
